import React, { useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import CustomModal from 'components/CustomModal/CustomModal'
import { hideHtmlAnalysisModal } from '../../actions'
import { runHtmlAnalysis, getHtmlAnalysis, fixHtmlAnalysis, cancelHtmlAnalysis } from '../../services/site'
import styles from './HtmlAnalysisModal.module.css'
import SuccessIcon from '../SuccessIcon/SuccessIcon'

const IN_FLIGHT = ['pending', 'analysing', 'fixing']
const POLL_MS = 3000

const SEVERITY_ORDER = ['critical', 'warning', 'informational']
const SEVERITY_LABEL = { critical: 'Critical', warning: 'Warning', informational: 'Info' }
const SEVERITY_ICON = { critical: '🔴', warning: '🟡', informational: '⚪' }

const Spinner = () => <div className={styles.spinner} />

const IssueRow = ({ issue }) => {
  const [expanded, setExpanded] = useState(true)
  const isAutoFixable = (issue.type === 'case-mismatch' && !!issue.actualFile) || !!issue.llmFixSuggestion
  const hasExplanation = isAutoFixable || !!issue.llmExplanation
  return (
    <div
      className={`${styles.issueRow} ${expanded ? styles.issueRowExpanded : ''} ${issue.fixed ? styles.issueRowFixed : ''}`}
    >
      <div
        className={`${styles.issueRowHeader} ${hasExplanation ? styles.issueRowClickable : ''}`}
        onClick={() => hasExplanation && setExpanded((e) => !e)}
      >
        <span className={styles.issueIcon}>
          {issue.fixed ? (
            '✅'
          ) : issue.severity === 'warning' ? (
            <img src="/assets/icons/warning-yellow.svg" alt="Warning" width="16" height="16" />
          ) : (
            SEVERITY_ICON[issue.severity] || '⚪'
          )}
        </span>
        <div className={styles.issueBody}>
          <div className={styles.issueFileRow}>
            <span className={styles.issueFile}>{issue.file}</span>
            {issue.badge && <span className={styles.issueTypeBadge}>{issue.badge}</span>}
          </div>
          {(issue.detail || issue.ref) && <code className={styles.issueDetail}>{issue.detail || issue.ref}</code>}
        </div>
        <div className={styles.issueMeta}>
          <span className={`${styles.issueBadge} ${styles[issue.severity]}`}>
            {issue.fixed ? 'Fixed' : SEVERITY_LABEL[issue.severity] || issue.severity}
          </span>
          {hasExplanation && <span className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}>▾</span>}
        </div>
      </div>
      {hasExplanation && (
        <div className={styles.issueExplanation}>
          <div className={`${styles.issueExplanationInner} ${styles[`explanation_${issue.severity}`]}`}>
            {isAutoFixable ? (
              <span className={styles.autoFixLabel}>✨ This will be corrected automatically when you click Fix.</span>
            ) : (
              <>
                <span className={styles.howToFixLabel}>How to fix:</span> {issue.llmExplanation}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const IssueList = ({ issues }) => {
  const grouped = SEVERITY_ORDER.reduce((acc, sev) => {
    const group = issues.filter((i) => i.severity === sev)
    if (group.length) acc[sev] = group
    return acc
  }, {})
  return (
    <div className={styles.issueList}>
      {Object.entries(grouped).map(([sev, items]) => (
        <div key={sev} className={styles.severityGroup}>
          <div className={`${styles.severityHeader} ${styles[`sev_${sev}`]}`}>
            {SEVERITY_ICON[sev]} {SEVERITY_LABEL[sev]} <span className={styles.sevCount}>({items.length})</span>
          </div>
          {items.map((issue, i) => (
            <IssueRow key={i} issue={issue} />
          ))}
        </div>
      ))}
    </div>
  )
}

const RunningState = ({ message, onMinimise, onCancel }) => (
  <div className={styles.runningState}>
    <Spinner />
    <p className={styles.stepMessage}>
      {message || 'Analysing your site'}
      <span className={styles.dots}>
        <span />
        <span />
        <span />
      </span>
    </p>
    {(onMinimise || onCancel) && (
      <div className={styles.runningActions}>
        {onMinimise && (
          <Button variant="outline-secondary" size="sm" onClick={onMinimise}>
            Minimise
          </Button>
        )}
        {onCancel && (
          <Button variant="primary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    )}
  </div>
)

const MinimisedBar = ({ message, domain, onExpand }) => (
  <div className={styles.minimisedBar}>
    <Spinner />
    <span className={styles.minimisedMessage}>{message || 'Analysing'}</span>
    <span className={styles.minimisedDomain}>{domain}</span>
    <Button variant="outline-primary" size="sm" className={styles.expandBtn} onClick={onExpand}>
      View
    </Button>
  </div>
)

const ErrorState = ({ message, onRetry }) => (
  <div className={styles.errorState}>
    <p className={styles.errorMessage}>⚠️ {message || 'Something went wrong. Please try again.'}</p>
    <Button onClick={onRetry}>Try again</Button>
  </div>
)

const FixPreview = ({ issues, onConfirm, onCancel, loading }) => {
  const fixable = issues.filter((i) => !i.fixed && !!i.llmFixSuggestion)
  return (
    <div className={styles.fixPreview}>
      <h4 className={styles.fixPreviewTitle}>Review fixes</h4>
      <p className={styles.fixPreviewSubtitle}>
        {fixable.length} change{fixable.length !== 1 ? 's' : ''} will be applied to your site files.
      </p>
      <div className={styles.fixPreviewList}>
        {fixable.map((issue, i) => (
          <div key={i} className={styles.fixPreviewItem}>
            <span className={styles.fixPreviewFile}>{issue.file}</span>
            <div className={styles.fixPreviewChange}>
              <s className={styles.fixPreviewOld}>{issue.ref}</s>
              <span className={styles.fixPreviewArrow}>→</span>
              <span className={styles.fixPreviewNew}>{issue.llmFixSuggestion.correctedRef}</span>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.fixPreviewActions}>
        <Button onClick={onConfirm} disabled={loading}>
          {loading ? 'Applying…' : 'Apply fixes'}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

const HtmlAnalysisModal = ({ site }) => {
  const dispatch = useDispatch()
  const [analysis, setAnalysis] = useState(() => site?.htmlAnalysis || null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [minimised, setMinimised] = useState(false)
  const [showFixPreview, setShowFixPreview] = useState(false)
  const pollRef = useRef(null)
  const domain = site?.subdomain

  const status = analysis?.status
  const isInFlight = IN_FLIGHT.includes(status)
  const isComplete = status === 'complete' || status === 'fixed'
  const isError = status === 'error'
  const isIdle = !status

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const fetchAnalysis = async () => {
    try {
      const res = await getHtmlAnalysis(domain)
      const data = res?.data?.htmlAnalysis
      if (data) {
        setAnalysis(data)
        if (!IN_FLIGHT.includes(data?.status)) {
          stopPolling()
          setMinimised(false)
        }
      }
    } catch {
      /* keep polling */
    }
  }

  const startPolling = () => {
    stopPolling()
    pollRef.current = setInterval(fetchAnalysis, POLL_MS)
  }

  // Fetch fresh state on mount to override the stale Redux snapshot
  useEffect(() => {
    fetchAnalysis()
  }, [])

  useEffect(() => {
    if (isInFlight) startPolling()
    return stopPolling
  }, [status])

  const handleRun = async () => {
    setError(null)
    setShowFixPreview(false)
    setAnalysis((prev) => ({ ...prev, status: 'pending', currentStepMessage: 'Queuing analysis' }))
    try {
      await runHtmlAnalysis(domain)
      startPolling()
    } catch (e) {
      setAnalysis((prev) => ({ ...prev, status: 'error', currentStepMessage: null }))
      setError(e?.response?.data?.message || 'Failed to start analysis.')
    }
  }

  const handleCancel = async () => {
    try {
      await cancelHtmlAnalysis(domain)
      stopPolling()
      setMinimised(false)
      setAnalysis((prev) => ({ ...prev, status: null, currentStepMessage: null }))
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to cancel.')
    }
  }

  const handleFixConfirm = async () => {
    setLoading(true)
    setError(null)
    setShowFixPreview(false)
    setAnalysis((prev) => ({ ...prev, status: 'fixing', currentStepMessage: 'Applying fixes' }))
    try {
      await fixHtmlAnalysis(domain)
      startPolling()
    } catch (e) {
      setAnalysis((prev) => ({ ...prev, status: 'complete' }))
      setError(e?.response?.data?.message || 'Failed to apply fixes.')
    } finally {
      setLoading(false)
    }
  }

  const onHide = () => dispatch(hideHtmlAnalysisModal())

  const issues = analysis?.issues || []
  const criticalCount = issues.filter((i) => i.severity === 'critical' && !i.fixed).length
  const fixableCount = issues.filter((i) => !i.fixed && !!i.llmFixSuggestion).length
  const fixedCount = issues.filter((i) => i.fixed).length
  const analyzedAt = analysis?.analyzedAt
  const fixedAt = analysis?.fixedAt

  if (minimised) {
    return <MinimisedBar message={analysis?.currentStepMessage} domain={domain} onExpand={() => setMinimised(false)} />
  }

  return (
    <CustomModal show centered onHide={onHide} enforceFocus={false}>
      <div className="p-4">
        <Modal.Header closeButton className={styles.header}>
          <Modal.Title className={styles.title}>Site Analysis</Modal.Title>
        </Modal.Header>

        <Modal.Body className={styles.body}>
          {isIdle && !loading && (
            <div className={styles.idleState}>
              <p className={styles.idleText}>
                We&apos;ll analyse your site for any possible issues and suggest fixes and improvements.
              </p>
              <div className={styles.idleActions}>
                <Button onClick={handleRun}>Run analysis</Button>
              </div>
            </div>
          )}

          {(isInFlight || (loading && !showFixPreview)) && (
            <RunningState
              message={analysis?.currentStepMessage}
              onMinimise={isInFlight ? () => setMinimised(true) : undefined}
              onCancel={isInFlight ? handleCancel : undefined}
            />
          )}
          {(isError || error) && !isInFlight && (
            <ErrorState message={analysis?.errorMessage || error} onRetry={handleRun} />
          )}

          {isComplete && !showFixPreview && (
            <div className={styles.reportState}>
              <div className={styles.reportMeta}>
                {analyzedAt && (
                  <span className={styles.timestamp}>
                    Last analyzed {new Date(fixedAt || analyzedAt).toLocaleString()}
                  </span>
                )}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className={styles.rerunOutlineBtn}
                  onClick={handleRun}
                  disabled={loading}
                >
                  Re-run
                </Button>
              </div>

              {issues.length === 0 ? (
                <div className={styles.allClear}>
                  <SuccessIcon width={40} height={40} />
                  <p>No issues found. Your site looks good!</p>
                </div>
              ) : (
                <>
                  <div className={styles.summary}>
                    {criticalCount > 0 && (
                      <span className={`${styles.chip} ${styles.critical}`}>{criticalCount} critical</span>
                    )}
                    {fixedCount > 0 && <span className={`${styles.chip} ${styles.fixed}`}>{fixedCount} fixed</span>}
                    <span className={styles.chip}>{issues.length} total</span>
                  </div>

                  <IssueList issues={issues} />

                  {fixableCount > 0 && (
                    <Button variant="primary" onClick={() => setShowFixPreview(true)} disabled={loading}>
                      {`Fix ${fixableCount} issue${fixableCount === 1 ? '' : 's'}`}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {isComplete && showFixPreview && (
            <FixPreview
              issues={issues}
              onConfirm={handleFixConfirm}
              onCancel={() => setShowFixPreview(false)}
              loading={loading}
            />
          )}
        </Modal.Body>
      </div>
    </CustomModal>
  )
}

export default HtmlAnalysisModal
